import { useState, useCallback, useEffect, createContext, useContext, useRef } from 'react'

// ─── Types ────────────────────────────────────────────────────────────────────

type ToastKind = 'info' | 'success' | 'error'

interface ToastItem {
  id: number
  message: string
  kind: ToastKind
}

interface ToastContextValue {
  toast: (message: string, kind?: ToastKind) => void
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

// ─── Single Toast ─────────────────────────────────────────────────────────────

const KIND_STYLE: Record<ToastKind, { bg: string; color: string; border: string; icon: string }> = {
  success: { bg: '#EBFAF2', color: '#1B5E3B', border: '#B8D9C4', icon: '✓' },
  error:   { bg: '#FFF5F5', color: '#C53030', border: '#FED7D7', icon: '✕' },
  info:    { bg: 'var(--warm)', color: 'var(--ink)', border: 'var(--border)', icon: 'ℹ' },
}

function ToastMessage({ item, onRemove }: { item: ToastItem; onRemove: (id: number) => void }) {
  const s = KIND_STYLE[item.kind]
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Trigger enter animation
    const t1 = setTimeout(() => setVisible(true), 10)
    // Start exit after 3.5 s
    const t2 = setTimeout(() => setVisible(false), 3500)
    // Remove after exit animation completes
    const t3 = setTimeout(() => onRemove(item.id), 3800)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [item.id, onRemove])

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 16px',
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-md)',
        color: s.color,
        fontSize: 13,
        fontWeight: 500,
        maxWidth: 340,
        pointerEvents: 'auto',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.97)',
        transition: 'opacity 0.22s ease, transform 0.22s ease',
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: s.color,
          color: s.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {s.icon}
      </span>
      {item.message}
    </div>
  )
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const counter = useRef(0)

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback((message: string, kind: ToastKind = 'info') => {
    counter.current += 1
    setToasts((prev) => [...prev, { id: counter.current, message, kind }])
  }, [])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast stack — bottom-right */}
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          alignItems: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <ToastMessage key={t.id} item={t} onRemove={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}
